import { GreeksFormatter } from '@shared/commons/cache/OptionMaster/OptionTrader/GreeksFormatter';
import { OptionTraderUtils } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionTraderUtils';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { PanelDisplayMode } from '@shared/commons/CustomerAccess/PanelDisplayMode';
import { DataCache } from '@shared/commons/DataCache';
import { DynProperty } from '@shared/commons/DynProperty';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { QuotingType } from '@shared/utils/Instruments/QuotingType';
import { QuickTableComparingType } from '@shared/utils/QuickTableMisc/QuickTableComparingType';
import { StringUtils } from '@shared/utils/StringUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { TradingUtils } from '@shared/utils/Trading/TradingUtils';
import { QTAlignment } from '../../elements/QuickTable/QuickTableColumn';
import { QuickTableEditingInfo } from '../../elements/QuickTable/QuickTableMisc';
import { ThemeManager } from '../../misc/ThemeManager';
import { WDSettings } from '../../settings/WDGeneralSettings';
import { PanelNames } from '../../UtilsClasses/FactoryConstants';
import { WDSettingsUtils } from '../../UtilsClasses/WDGeneralSettingsUtils';
import { BaseItem, ColumnData } from '../BaseItem';
import { ColumnParams } from '../ColumnParams';
import { TableTotalsVisibility } from '../TableTotalsVisibility';
export class OptionChainTableItem extends BaseItem {
    constructor(putCall, instrument, optionTrader) {
        super();
        this.isNeedFireUpdate = false;
        this._putCall = putCall;
        this.instrument = instrument;
        this._optionTrader = optionTrader;
        this.subscribe();
        if (!isNullOrUndefined(instrument)) {
            this.QuickTableEditingInfoMap[OptionChainTableItem.PAPER_POSITION_COL_INDEX] = new QuickTableEditingInfo(DynProperty.DOUBLE);
        }
        this.updateEditingInfo();
    }
    getQuickTableCellForeground(column) {
        const colors = ThemeManager.colors;
        if (column === OptionChainTableItem.PAPER_POSITION_COL_INDEX) {
            const value = this.getColumnValue(column);
            if (value > 0) {
                return colors.buyColor;
            }
            else if (value < 0) {
                return colors.sellColor;
            }
            else {
                return null;
            }
        }
        else {
            return super.getQuickTableCellForeground(column);
        }
    }
    Dispose() {
        this.unsubscribe();
        super.Dispose();
    }
    updateEditingInfo() {
        const optionTrader = this._optionTrader;
        if (isNullOrUndefined(optionTrader)) {
            return;
        }
        const account = this._optionTrader.account;
        const instrument = this.instrument;
        if (isNullOrUndefined(instrument)) {
            return;
        }
        const paperPosition = this._optionTrader.getPaperPosition(instrument);
        let productType = ProductType.General;
        if (isNullOrUndefined(paperPosition) === false) {
            productType = paperPosition.ProductType;
        }
        else {
            const allowedProductTypes = TradingUtils.getAllowedProductTypesForTrading(account, instrument);
            if (allowedProductTypes.length > 0) {
                productType = allowedProductTypes[0];
            }
        }
        const settings = Quantity.getQuantityNumericSettings(instrument, account, WDSettingsUtils.displayAmountInLots(), productType);
        const amountNumeric = this.QuickTableEditingInfoMap[OptionChainTableItem.PAPER_POSITION_COL_INDEX];
        amountNumeric.Min = -settings.maxValue;
        amountNumeric.Max = settings.maxValue;
        amountNumeric.Inc = settings.step;
        amountNumeric.DecimalPlaces = settings.decimalPrecision;
        amountNumeric.IsEditable = OptionTraderUtils.getDaysToExpirationForInstrument(instrument) >= 0;
    }
    subscribe() {
        const instrument = this.instrument;
        if (isNullOrUndefined(instrument)) {
            return;
        }
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_TRADES);
        DataCache.FQuoteCache.addListener(instrument, this, HistoryType.QUOTE_INSTRUMENT_DAY_BAR);
    }
    unsubscribe() {
        const instrument = this.instrument;
        if (isNullOrUndefined(instrument)) {
            return;
        }
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_LEVEL1);
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_TRADES);
        DataCache.FQuoteCache.removeListener(instrument, this, HistoryType.QUOTE_INSTRUMENT_DAY_BAR);
    }
    newQuote(quote) {
        this.isNeedFireUpdate = true;
    }
    GetItemId() {
        return this.ItemId;
    }
    getMonetaryValue() {
        const lastPrice = this.instrument.Level1.GetLastPrice(this._optionTrader.account);
        if (isNaN(lastPrice)) {
            return NaN;
        }
        const account = this._optionTrader.account;
        const instrument = this.instrument;
        const crossPrice = DataCache.CrossRateCache.GetCrossPriceExp1Exp2(instrument.Exp2, account.assetBalanceDefault.Asset.Name, account);
        const tickCost = instrument.QuotingType === QuotingType.LotSize ? instrument.LotSize : instrument.GetTickCost();
        return lastPrice * tickCost * crossPrice;
    }
    formatPrice(price) {
        if (!isValidNumber(price) || price === Infinity) {
            return OptionChainTableItem.NOT_AVAILABLE;
        }
        else {
            return this.instrument.formatPrice(price);
        }
    }
    getColumnValue(column) {
        const account = this._optionTrader.account;
        const instrument = this.instrument;
        const hasInstrument = !isNullOrUndefined(instrument);
        const showLots = WDSettings.isQuantityInLots;
        switch (column) {
            case OptionChainTableItem.LAST_PRICE_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetLastPrice(account) : NaN;
            case OptionChainTableItem.IV_COL_INDEX:
                return hasInstrument
                    ? GreeksFormatter.roundIV(this._optionTrader.getGreeks(this._putCall, instrument).ivPercent)
                    : NaN;
            case OptionChainTableItem.PAPER_POSITION_COL_INDEX: {
                if (!hasInstrument) {
                    return 0;
                }
                const paperPositon = this._optionTrader.getPaperPosition(instrument);
                if (isNullOrUndefined(paperPositon)) {
                    return 0;
                }
                else {
                    const amount = Quantity.convertLotsToAmount(paperPositon.QuantityLots, paperPositon.Instrument, true, paperPositon.Account, paperPositon.ProductType);
                    return paperPositon.Operation === OperationType.Buy ? amount : -amount;
                }
            }
            case OptionChainTableItem.GAMMA_COL_INDEX:
                return hasInstrument
                    ? GreeksFormatter.roundGreek(this._optionTrader.getGreeks(this._putCall, instrument).gamma)
                    : NaN;
            case OptionChainTableItem.ASK_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetAsk(account) : NaN;
            case OptionChainTableItem.BID_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetBid(account) : NaN;
            case OptionChainTableItem.ASK_SIZE_COL_INDEX:
                return hasInstrument ? instrument.Level1.getAskSize(showLots) : NaN;
            case OptionChainTableItem.BID_SIZE_COL_INDEX:
                return hasInstrument ? instrument.Level1.getBidSize(showLots) : NaN;
            case OptionChainTableItem.DELTA_COL_INDEX:
                return hasInstrument
                    ? GreeksFormatter.roundGreek(this._optionTrader.getGreeks(this._putCall, instrument).delta)
                    : NaN;
            case OptionChainTableItem.THETA_COL_INDEX:
                return hasInstrument
                    ? GreeksFormatter.roundGreek(this._optionTrader.getGreeks(this._putCall, instrument).theta)
                    : NaN;
            case OptionChainTableItem.VEGA_COL_INDEX:
                return hasInstrument
                    ? GreeksFormatter.roundGreek(this._optionTrader.getGreeks(this._putCall, instrument).vega)
                    : NaN;
            case OptionChainTableItem.VOLUME_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetVolume() : NaN;
            case OptionChainTableItem.MONETARY_VALUE_COL_INDEX:
                return hasInstrument ? this.getMonetaryValue() : NaN;
            case OptionChainTableItem.RHO_COL_INDEX:
                return hasInstrument
                    ? GreeksFormatter.roundGreek(this._optionTrader.getGreeks(this._putCall, instrument).rho)
                    : NaN;
            case OptionChainTableItem.CHANGE_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetChange(account) : NaN;
            case OptionChainTableItem.CHANGE_PERCENT_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetChangePercent(account) : NaN;
            case OptionChainTableItem.OPEN_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetOpen(account) : NaN;
            case OptionChainTableItem.HIGH_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetHigh(account) : NaN;
            case OptionChainTableItem.LOW_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetLow(account) : NaN;
            case OptionChainTableItem.PREV_CLOSE_COL_INDEX:
                return hasInstrument ? instrument.Level1.GetPrevClose(account) : NaN;
            case OptionChainTableItem.LAST_TIME_COL_INDEX: {
                if (!hasInstrument)
                    return null;
                const lastMessageTime = instrument.Level1.lastMessageTime;
                if (isNullOrUndefined(lastMessageTime))
                    return null;
                if (lastMessageTime.getTime() === 0)
                    return null;
                return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(lastMessageTime);
            }
            case OptionChainTableItem.OPEN_INTEREST_COL_INDEX:
                return hasInstrument ? instrument.Level1.openInterest : NaN;
        }
        return '';
    }
    getColumnData(column) {
        var _a, _b;
        const account = this._optionTrader.account;
        const instrument = this.instrument;
        const value = this.getColumnValue(column);
        const showLots = WDSettings.isQuantityInLots;
        const paperPosition = (_a = this._optionTrader) === null || _a === void 0 ? void 0 : _a.getPaperPosition(instrument);
        let formattedValue = '';
        if (isNullOrUndefined(instrument)) {
            return new ColumnData(value, formattedValue);
        }
        switch (column) {
            case OptionChainTableItem.LAST_PRICE_COL_INDEX:
            case OptionChainTableItem.ASK_COL_INDEX:
            case OptionChainTableItem.BID_COL_INDEX:
            case OptionChainTableItem.OPEN_COL_INDEX:
            case OptionChainTableItem.HIGH_COL_INDEX:
            case OptionChainTableItem.LOW_COL_INDEX:
            case OptionChainTableItem.PREV_CLOSE_COL_INDEX:
            case OptionChainTableItem.CHANGE_COL_INDEX:
                formattedValue = this.formatPrice(value);
                break;
            case OptionChainTableItem.ASK_SIZE_COL_INDEX:
            case OptionChainTableItem.BID_SIZE_COL_INDEX:
            case OptionChainTableItem.VOLUME_COL_INDEX:
            case OptionChainTableItem.OPEN_INTEREST_COL_INDEX:
                formattedValue = value >= 0 ? value.toString() : OptionChainTableItem.NOT_AVAILABLE;
                break;
            case OptionChainTableItem.CHANGE_PERCENT_COL_INDEX:
                formattedValue = instrument.Level1.strChangePercent(account);
                break;
            case OptionChainTableItem.IV_COL_INDEX:
                formattedValue = GreeksFormatter.formatIV(value);
                break;
            case OptionChainTableItem.GAMMA_COL_INDEX:
            case OptionChainTableItem.DELTA_COL_INDEX:
            case OptionChainTableItem.THETA_COL_INDEX:
            case OptionChainTableItem.VEGA_COL_INDEX:
            case OptionChainTableItem.RHO_COL_INDEX:
                formattedValue = GreeksFormatter.formatGreek(value);
                break;
            case OptionChainTableItem.PAPER_POSITION_COL_INDEX:
                formattedValue = DataCache.formatVolume(instrument, value, showLots, (_b = paperPosition === null || paperPosition === void 0 ? void 0 : paperPosition.ProductType) !== null && _b !== void 0 ? _b : ProductType.General, account);
                break;
            case OptionChainTableItem.MONETARY_VALUE_COL_INDEX:
                formattedValue = value > 0 ? account.assetBalanceDefault.formatPrice(value) : StringUtils.NOT_AVAILABLE;
                break;
            case OptionChainTableItem.LAST_TIME_COL_INDEX:
                formattedValue = !isNullOrUndefined(value)
                    ? DateTimeUtils.FormatToTime(value)
                    : StringUtils.NOT_AVAILABLE;
                break;
        }
        return new ColumnData(value, formattedValue);
    }
    GetTooltipKey(column) {
        switch (column) {
            case OptionChainTableItem.MONETARY_VALUE_COL_INDEX:
                return 'panel.optionChain.monetaryValue.tt';
        }
        return '';
    }
    // #region ITableItem methods
    ColumnCount() {
        return OptionChainTableItem.columnsParams.length;
    }
    GetColumnParams(i) {
        const params = OptionChainTableItem.columnsParams[i];
        params.Hidden = params.DefaultHidden;
        switch (i) {
            case OptionChainTableItem.PAPER_POSITION_COL_INDEX:
                params.Hidden =
                    CustomerAccess.GetDisplayMode(PanelNames.OptionPaperPositionsPanel) === PanelDisplayMode.NotAllowed;
                break;
        }
        return params;
    }
    GetDataType(columnIndex) {
        return OptionChainTableItem.dataTypes[columnIndex];
    }
}
// #endregion
// #region static table parameters
OptionChainTableItem.NOT_AVAILABLE = StringUtils.THREE_DASHES;
OptionChainTableItem.LAST_PRICE_COL_INDEX = 0;
OptionChainTableItem.BID_SIZE_COL_INDEX = 1;
OptionChainTableItem.IV_COL_INDEX = 2;
OptionChainTableItem.PAPER_POSITION_COL_INDEX = 3;
OptionChainTableItem.ASK_SIZE_COL_INDEX = 4;
OptionChainTableItem.DELTA_COL_INDEX = 5;
OptionChainTableItem.GAMMA_COL_INDEX = 6;
OptionChainTableItem.VEGA_COL_INDEX = 7;
OptionChainTableItem.THETA_COL_INDEX = 8;
OptionChainTableItem.RHO_COL_INDEX = 9;
OptionChainTableItem.VOLUME_COL_INDEX = 10;
OptionChainTableItem.PREV_CLOSE_COL_INDEX = 11;
OptionChainTableItem.OPEN_INTEREST_COL_INDEX = 12;
OptionChainTableItem.MONETARY_VALUE_COL_INDEX = 13;
OptionChainTableItem.CHANGE_COL_INDEX = 14;
OptionChainTableItem.CHANGE_PERCENT_COL_INDEX = 15;
OptionChainTableItem.OPEN_COL_INDEX = 16;
OptionChainTableItem.HIGH_COL_INDEX = 17;
OptionChainTableItem.LOW_COL_INDEX = 18;
OptionChainTableItem.LAST_TIME_COL_INDEX = 19;
OptionChainTableItem.ASK_COL_INDEX = 20;
OptionChainTableItem.BID_COL_INDEX = 21;
OptionChainTableItem.columnsParams = [
    new ColumnParams('panel.optionChain.lastPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.bidSize', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.iv', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.paperPosition', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.askSize', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.delta', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.gamma', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.vega', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.theta', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.rho', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.volume', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.prevClose', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.openInterest', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.optionChain.monetaryValue', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.change', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.changePercent', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.open', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.high', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.low', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.lastTime', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.optionChain.ask', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.optionChain.bid', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true)
];
OptionChainTableItem.dataTypes = [
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.Double,
    QuickTableComparingType.DateTime,
    QuickTableComparingType.Double
];
OptionChainTableItem.prevValueColumnsIndexes = [
    OptionChainTableItem.LAST_PRICE_COL_INDEX,
    OptionChainTableItem.IV_COL_INDEX,
    OptionChainTableItem.GAMMA_COL_INDEX,
    OptionChainTableItem.ASK_SIZE_COL_INDEX,
    OptionChainTableItem.BID_SIZE_COL_INDEX,
    OptionChainTableItem.DELTA_COL_INDEX,
    OptionChainTableItem.THETA_COL_INDEX,
    OptionChainTableItem.VEGA_COL_INDEX,
    OptionChainTableItem.MONETARY_VALUE_COL_INDEX,
    OptionChainTableItem.RHO_COL_INDEX
];
